import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../fonts/breuer-headline.css";
import "./character.css";
import { setBackButtonUrl } from "../uiSlice";
import { getHouseById } from "../house/housesConfig";
import { getCharactersForHouse } from "./charactersConfig";
import selectBtnImg from "../images/select.png";

const HOGWARTS_SELECTION_KEY_BASE = "hogwartsSelectionData";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function getSelectionStorageKey(userLike) {
  const stored = readStoredUserData();
  const uid = userLike?.userId ?? userLike?.userid ?? userLike?.id ?? stored?.userId ?? stored?.userid ?? stored?.id;
  const sid = userLike?.sessionId ?? stored?.sessionId;
  const oid = userLike?.organizationId ?? stored?.organizationId;
  const scope = [uid, sid, oid]
    .map((v) => String(v ?? "").trim())
    .join(":");
  return scope.replace(/:/g, "") ? `${HOGWARTS_SELECTION_KEY_BASE}:${scope}` : HOGWARTS_SELECTION_KEY_BASE;
}

function readSelectionArray(userLike) {
  try {
    const raw = localStorage.getItem(getSelectionStorageKey(userLike));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function findSelectionEntry(type, userLike) {
  return readSelectionArray(userLike).find((item) => item?.type === type) ?? null;
}

function upsertSelectionEntry(type, value, userLike) {
  const current = readSelectionArray(userLike);
  const filtered = current.filter((item) => item?.type !== type);
  filtered.push({ type, ...value });
  try {
    localStorage.setItem(getSelectionStorageKey(userLike), JSON.stringify(filtered));
  } catch {
    // ignore storage write failures
  }
}

function Character() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, user } = useSelector((state) => state.auth);
  const selectedHouse = findSelectionEntry("house", user);
  const houseId = location.state?.houseId ?? selectedHouse?.houseId;
  const house = houseId ? getHouseById(houseId) : null;
  const characters = useMemo(
    () => getCharactersForHouse(houseId),
    [houseId]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    document.documentElement.classList.add("character-static-bg");
    document.body.classList.add("character-static-bg");
    return () => {
      document.documentElement.classList.remove("character-static-bg");
      document.body.classList.remove("character-static-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/house"));
  }, [dispatch, status, user]);

  useEffect(() => {
    setIndex(0);
  }, [houseId]);

  if (!house || characters.length === 0) {
    return <Navigate to="/house" replace />;
  }

  const safeIndex = ((index % characters.length) + characters.length) % characters.length;
  const current = characters[safeIndex];

  const goPrev = () => {
    setIndex((i) => i - 1);
  };

  const goNext = () => {
    setIndex((i) => i + 1);
  };

  const handleSelect = () => {
    upsertSelectionEntry("character", {
      houseId: house.id,
      characterId: current.id,
      characterName: current.name,
    }, user);
    navigate("/wand", {
      state: {
        houseId: house.id,
        characterId: current.id,
        characterName: current.name,
      },
    });
  };

  return (
    <div className="character-page">
      <div className="character-layout">
        <header className="character-scroll">
          <p className="character-scroll-title">Select Your Champion of Magic
          </p>
          <p className="character-scroll-sub">{house.name}</p>
        </header>

        <div className="character-frame">
          <div className="character-frame-inner">
            <div className="character-parchment">
              <div className="character-carousel">
                <button
                  type="button"
                  className="character-arrow character-arrow--prev"
                  onClick={goPrev}
                  aria-label="Previous character"
                >
                  ‹
                </button>
                <div className="character-portrait-wrap">
                  <img
                    src={current.portrait}
                    alt={current.name}
                    className="character-portrait"
                  />
                </div>
                <button
                  type="button"
                  className="character-arrow character-arrow--next"
                  onClick={goNext}
                  aria-label="Next character"
                >
                  ›
                </button>
              </div>

              <div className="character-details">
                <h2 className="character-name">{current.name}</h2>
                <p className="character-desc">{current.description}</p>
                <div className="character-select-row">
                  <button
                    type="button"
                    className="character-select-btn"
                    onClick={handleSelect}
                  >
                    <img src={selectBtnImg} alt="Select this character" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Character;
